import * as React from "react";
import {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {EmailOutlined, LockOutlined, Visibility, VisibilityOff} from "@mui/icons-material";
import AuthService from "../../services/AuthService";
import {APP_DATA, CALLBACK_URL} from "../../config/settings";
import fullLogo from "../../asset/icons/DriveFy.png";
import "./login.css";

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [tab, setTab]           = useState("login");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors]     = useState([]);

  /* OAuth token callback */
  useEffect(() => {
    const params  = new URLSearchParams(location.search);
    const idToken = params.get("idToken");
    if (idToken) {
      localStorage.setItem("idToken", idToken);
      localStorage.setItem("authToken", idToken);
      const rt = params.get("refreshToken");
      if (rt) localStorage.setItem("refreshToken", rt);
      localStorage.setItem("callback", CALLBACK_URL);
      navigate(APP_DATA.WELCOME_ROUTER);
    }
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!email || !password) return;
    const res = await AuthService.login(email, password);
    if (res.success) {
      const payload = res.data.idToken.payload || {};
      // Same keys the webapp's loginUser() writes, so the v2 idToken is shared.
      localStorage.setItem("idToken",      res.data.idToken.jwtToken);
      localStorage.setItem("authToken",    res.data.idToken.jwtToken);
      localStorage.setItem("refreshToken", res.data.refreshToken.token);
      localStorage.setItem("userId",       payload.sub || "");
      localStorage.setItem("username",     email);
      localStorage.setItem("userName",     payload.name || "");
      localStorage.setItem("lastLoginAt",  String(Date.now()));
      // Drive's DriveUserStatus/FileTree still read this payload blob.
      localStorage.setItem("user",         JSON.stringify(payload));
      navigate(APP_DATA.WELCOME_ROUTER);
    } else {
      setErrors(Array.isArray(res.msg) ? res.msg : [res.msg]);
    }
  };

  return (
    <div className="page">

      {/* Logo */}
      <div className="logo">
        <img src={fullLogo} alt="SmileFy" />
      </div>

      {/* Card */}
      <div className="authPanel">

        {/* Tabs */}
        <div className="tabs">
          <div className={`tab ${tab === "login"  ? "tabActive" : ""}`} onClick={() => setTab("login")}>{t('login.loginTab')}</div>
          <div className={`tab ${tab === "signup" ? "tabActive" : ""}`} onClick={() => setTab("signup")}>{t('login.signUpTab')}</div>
        </div>

        {/* Form */}
        <form className="form" onSubmit={handleSubmit} noValidate>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="errors">
              {errors.map((e, i) => <div key={i} className="errorItem">{e}</div>)}
            </div>
          )}

          {/* Email */}
          <div className="field">
            <label className="fieldLabel">{t('login.emailLabel')}</label>
            <div className="inputRow">
              <span className="inputIcon"><EmailOutlined sx={{fontSize: 18}} /></span>
              <input className="input" type="email" placeholder={t('login.emailPlaceholder')}
                autoComplete="email" autoFocus value={email}
                onChange={e => setEmail(e.target.value)} />
            </div>
          </div>

          {/* Password */}
          <div className="field">
            <label className="fieldLabel">{t('login.passwordLabel')}</label>
            <div className="inputRow">
              <span className="inputIcon"><LockOutlined sx={{fontSize: 18}} /></span>
              <input className="input" type={showPassword ? "text" : "password"} placeholder="••••••••"
                autoComplete="current-password" value={password}
                onChange={e => setPassword(e.target.value)} />
              <button type="button" className="pwToggle"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                title={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                tabIndex={-1}>
                {showPassword ? <VisibilityOff sx={{fontSize: 18}} /> : <Visibility sx={{fontSize: 18}} />}
              </button>
            </div>
          </div>

          <button type="submit" className="loginBtn">{t('login.loginBtn')}</button>

          <span className="forgot">{t('login.forgotPassword')}</span>
        </form>
      </div>

      <p className="footer">{t('login.footer')}</p>
    </div>
  );
}
